import { Link } from '@tanstack/react-router'
import { Menu, X } from 'lucide-react'
import { useState } from 'react'
import { ThemeToggle } from '@/components/ThemeToggle'

interface PublicLayoutProps {
  children: React.ReactNode
}

/**
 * Public layout for marketing pages
 * Includes header with navigation and footer
 */
export function PublicLayout({ children }: PublicLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-950/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-2 text-2xl font-bold text-purple-400 transition-colors hover:text-purple-500"
            >
              <span>AerialFlow</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center space-x-8 md:flex">
              <a
                href="#features"
                className="text-sm font-medium text-slate-700 transition-colors hover:text-purple-400 dark:text-slate-300 dark:hover:text-purple-400"
              >
                Features
              </a>
              <a
                href="#pricing"
                className="text-sm font-medium text-slate-700 transition-colors hover:text-purple-400 dark:text-slate-300 dark:hover:text-purple-400"
              >
                Pricing
              </a>
              <a
                href="#for-studios"
                className="text-sm font-medium text-slate-700 transition-colors hover:text-purple-400 dark:text-slate-300 dark:hover:text-purple-400"
              >
                For Studios
              </a>
              <ThemeToggle />
              <Link
                to="/auth/login"
                search={{ redirect: '/app/dashboard' }}
                className="rounded-lg bg-purple-400 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-500"
              >
                Sign In
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-2 md:hidden">
              <ThemeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 md:hidden">
            <div className="space-y-1 px-4 pb-3 pt-2">
              <a
                href="#features"
                className="block rounded-lg px-3 py-2 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#pricing"
                className="block rounded-lg px-3 py-2 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </a>
              <a
                href="#for-studios"
                className="block rounded-lg px-3 py-2 text-base font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                For Studios
              </a>
              <Link
                to="/auth/login"
                search={{ redirect: '/app/dashboard' }}
                className="block rounded-lg bg-purple-400 px-3 py-2 text-base font-semibold text-white transition-colors hover:bg-purple-500"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            {/* Brand Column */}
            <div className="md:col-span-1">
              <span className="text-2xl font-bold text-purple-400">
                AerialFlow
              </span>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                Track your aerial journey and manage your studio with ease.
              </p>
            </div>

            {/* Product Column */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                Product
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    For Studios
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                Resources
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Community
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white">
                Company
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-sm text-slate-600 transition-colors hover:text-purple-400 dark:text-slate-400 dark:hover:text-purple-400"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 border-t border-slate-200 pt-8 dark:border-slate-800">
            <p className="text-center text-sm text-slate-600 dark:text-slate-400">
              © {new Date().getFullYear()} AerialFlow. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
